import './commitment-avatar.scss';

import type { StaticImageData } from 'next/image';
import type { PropsWithChildren, ReactNode } from 'react';
import React from 'react';

import { ChildImage } from '@sola/ark-core/atoms/child-image/child-image.tsx';
import { Image } from '@sola/ark-core/atoms/image/image.tsx';
import { ChildImageVariantEnum } from '@sola/ark-core/utils/child-image.ts';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { Either } from '@sola/schema/dotcom/helpers.ts';
import type { DisplayPurposeProps } from '@sola/schema/dotcom/shared/display.ts';
import type { GiftFundCodesEnum } from '@sola/schema/dotcom/shared/gifts.ts';
import {
  type GraphQLCommitment,
  GraphQLCommitmentType,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import type { IconNames } from '@sola/schema/lib/icons/icon-names.ts';
import { GiftIconMapByFundCode } from '@sola/shared-utils/cart/gifts.ts';

// NOTE Should this be a setting in SDG?
export const defaultIcon: IconNames = 'heart-hand';
const sizeMap = {
  sm: 36,
  lg: 48,
  xl: 88,
};

export type AvatarCommitment = Pick<GraphQLCommitment, 'participant'> & {
  commitmentType?: GraphQLCommitment['commitmentType'];
  fund?: Partial<GraphQLCommitment['fund']>;
  internalId?: GraphQLCommitment['internalId'];
};

export type WithoutText = {
  imgSrc?: string | StaticImageData;
  commitment?: AvatarCommitment;
};
export type WithText = {
  text: string;
};

type CommitmentAvatarWrapperProps = {
  className?: string;
  purpose?: DisplayPurposeProps['variants'];
  responsive?: boolean;
  size?: 'sm' | 'lg' | 'xl';
  isStacked?: boolean;
};

export type CommitmentAvatarProps = Either<WithText, WithoutText> &
  CommitmentAvatarWrapperProps;

type CommitmentAvatarContentProps = {
  commitment?: AvatarCommitment;
  imgSrc?: string | StaticImageData;
  responsive?: boolean;
  size: NonNullable<CommitmentAvatarWrapperProps['size']>;
  text?: string;
};

const getAvatarContent = ({
  commitment,
  imgSrc,
  responsive,
  size,
  text,
}: CommitmentAvatarContentProps): ReactNode => {
  if (text) {
    return <span className="label-md label-potent">{text}</span>;
  }
  if (imgSrc) {
    return (
      <Image
        className="absolute w-full h-full"
        src={imgSrc}
        alt=""
        width={48}
        height={48}
      />
    );
  }
  if (
    commitment?.commitmentType === GraphQLCommitmentType.ChildSponsorship ||
    commitment?.commitmentType === GraphQLCommitmentType.Correspondence
  ) {
    const imageSize = (responsive ? sizeMap.lg : sizeMap[size]) * 2;
    return commitment?.participant?.photoUrl ? (
      <ChildImage
        childImageUrl={commitment.participant.photoUrl}
        alt="Participant"
        variant={ChildImageVariantEnum.Circle}
        className="CommitmentAvatar__image"
        width={imageSize}
      />
    ) : (
      <ArkIcon iconName="beneficiary" />
    );
  }

  let iconName = commitment?.fund?.icon as IconNames;
  if (commitment?.commitmentType === GraphQLCommitmentType.GiftCommitment) {
    iconName =
      GiftIconMapByFundCode[commitment?.fund?.code as GiftFundCodesEnum] ??
      iconName;
  }

  return <ArkIcon iconName={iconName || defaultIcon} />;
};

// @TODO: Refactor this "wrapper" into it's own atom that can be used without "CommitmentAvatarWrapper" for things like TaxReceipt and other uses
export const CommitmentAvatarWrapper: React.FC<
  PropsWithChildren<CommitmentAvatarWrapperProps>
> = ({
  children,
  className,
  purpose,
  responsive,
  size = 'sm',
  isStacked = false,
}) => {
  const purposeClass = purpose ? `CommitmentAvatar--${purpose}` : null;

  return (
    <div
      className={cn(
        'CommitmentAvatar rounded-full flex items-center justify-center relative',
        [
          purposeClass,
          className,
          {
            'CommitmentAvatar--responsive md:label-xl': responsive,
            'CommitmentAvatar--large label-xl': size === 'lg',
            'CommitmentAvatar--xlarge label-xl': size === 'xl',
            'CommitmentAvatar--stacked': isStacked,
          },
        ],
      )}
    >
      {children}
    </div>
  );
};

export const CommitmentAvatar: React.FC<CommitmentAvatarProps> = ({
  commitment,
  text,
  imgSrc,
  size = 'sm',
  responsive,
  ...wrapperProps
}) => {
  const avatarContent = getAvatarContent({
    commitment,
    imgSrc,
    responsive,
    size,
    text,
  });

  return (
    <CommitmentAvatarWrapper
      responsive={responsive}
      size={size}
      {...wrapperProps}
    >
      {avatarContent}
    </CommitmentAvatarWrapper>
  );
};
